import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Loader2 } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import matchService, { PlayedMatch, MatchPredictionsResponse } from '../services/matchService'
import { formatDate, getMatchDate } from '../utils/dateHelper'
import { RootState } from '../store'
import MatchTeamsInline from './MatchTeamsInline'
import { footballDisplayScore, footballLiveBadge, FOOTBALL_LIVE_POLL_MS, isFootballMatchLive } from '../utils/footballLiveScore'

interface GamePredictionsModalProps {
  roomId: string
  match: PlayedMatch | null
  isOpen: boolean
  onClose: () => void
  demoPredictions?: Array<{
    _id: string
    playerName: string
    predictedScore: string
    winner: string | null
    predictedWinnerPoint?: number
    predictedScorePoint?: number
    totalPoints?: number
  }>
}

function isMatchStarted(match: { date: string; time?: string }): boolean {
  const t = new Date(getMatchDate(match, true)).getTime()
  return !Number.isNaN(t) && Date.now() >= t
}

const GamePredictionsModal: React.FC<GamePredictionsModalProps> = ({
  roomId,
  match,
  isOpen,
  onClose,
  demoPredictions,
}) => {
  const { t } = useTranslation()
  const { user: currentUser } = useSelector((state: RootState) => state.auth)
  const [data, setData] = useState<MatchPredictionsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const currentUserId = String((currentUser as any)?._id ?? (currentUser as any)?.id ?? '')

  useEffect(() => {
    if (!isOpen || !roomId || !match) {
      setData(null)
      return
    }
    if (demoPredictions?.length) {
      setData({
        match,
        predictions: demoPredictions.map((p) => ({
          _id: p._id,
          playerId: p._id,
          playerName: p.playerName,
          predictedScore: p.predictedScore,
          winner: p.winner,
          predictedWinnerPoint: p.predictedWinnerPoint ?? 0,
          predictedScorePoint: p.predictedScorePoint ?? 0,
          totalPoints: p.totalPoints ?? (p.predictedWinnerPoint ?? 0) + (p.predictedScorePoint ?? 0),
        })),
      })
      setLoading(false)
      return
    }
    let cancelled = false
    let isFirst = true
    const load = () => {
      if (isFirst) setLoading(true)
      matchService
        .getPredictionsForMatch(roomId, match._id)
        .then((res) => {
          if (!cancelled) setData(res)
        })
        .catch(() => {
          if (!cancelled && isFirst) setData(null)
        })
        .finally(() => {
          if (!cancelled && isFirst) {
            setLoading(false)
            isFirst = false
          }
        })
    }
    load()
    const interval = window.setInterval(load, FOOTBALL_LIVE_POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [isOpen, roomId, match, demoPredictions])

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  const m = data?.match ? { ...match, ...data.match } : match
  const matchNotStarted = m ? !isMatchStarted(m) : false
  const displayScore = footballDisplayScore(m)
  const liveBadge = footballLiveBadge(m)

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-2xl h-[80dvh] max-h-[80dvh] sm:h-[80vh] sm:max-h-[80vh] overflow-hidden flex flex-col min-h-0 glass-strong rounded-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 sm:p-5 border-b border-white/10 bg-gradient-to-b from-primary/10 to-transparent shrink-0">
          <div className="font-bold text-foreground text-lg sm:text-xl tracking-tight">
            {m ? (
              <MatchTeamsInline
                teamA={m.teamA}
                teamB={m.teamB}
                league={m.league}
                nameClassName="font-bold text-foreground text-lg sm:text-xl tracking-tight"
              />
            ) : (
              '–'
            )}
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            {m && formatDate(getMatchDate(m, true), true)}
          </div>
          {displayScore !== '' && (
            <div className={`mt-3 inline-flex items-center px-4 py-2 rounded-xl font-bold text-base border ${isFootballMatchLive(m) ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-primary/20 text-primary border-primary/30'}`}>
              {liveBadge ? `${liveBadge} · ` : ''}{t('roomPage.realScore')}: {displayScore}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          ) : !data?.predictions?.length ? (
            <div className="text-center py-10 text-muted-foreground text-sm sm:text-base">
              {t('roomPage.noPredictionsForMatch')}
            </div>
          ) : (
            <ul className="space-y-3">
              {data.predictions.map((pred) => {
                const isOwnPrediction = currentUserId && pred.playerId && String(pred.playerId) === currentUserId
                const hideFuture = matchNotStarted && !isOwnPrediction
                return (
                  <li
                    key={pred._id}
                    className="rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-border/50 bg-card hover:bg-muted/30 transition-colors"
                  >
                    <div className="font-bold text-foreground text-base">
                      {pred.playerName}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {t('stats.predicted')}:{' '}
                      {hideFuture ? `(${t('stats.predicted')})` : `${pred.predictedScore}${pred.winner ? ` (${pred.winner})` : ` (${t('stats.draw')})`}`}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 sm:gap-4 text-sm">
                      <span className="text-muted-foreground">
                        {t('stats.resultLabel')}:{' '}
                        <span className="font-semibold text-foreground">
                          {hideFuture ? 0 : (pred.predictedWinnerPoint ?? 0)}
                        </span>
                      </span>
                      <span className="text-muted-foreground">
                        {t('stats.scoreLabel')}:{' '}
                        <span className="font-semibold text-foreground">
                          {hideFuture ? 0 : (pred.predictedScorePoint ?? 0)}
                        </span>
                      </span>
                      {m?.playoff === true && (m?.winnerAdditionalTime != null && String(m.winnerAdditionalTime).trim() !== '' || pred.predictedWinnerAdditionalTime) && (() => {
                        const etActual = m?.winnerAdditionalTime != null ? String(m.winnerAdditionalTime).trim() : ''
                        const etPred = pred.predictedWinnerAdditionalTime != null ? String(pred.predictedWinnerAdditionalTime).trim() : ''
                        const etName = etActual || (!hideFuture && etPred ? etPred : '') || '–'
                        return (
                          <span className="text-muted-foreground">
                            {t('stats.winnerExtraTime')} ({etName}):{' '}
                            <span className="font-semibold text-foreground">
                              {hideFuture ? 0 : (pred.predictedWinnerAdditionalTimePoint ?? 0)}
                            </span>
                          </span>
                        )
                      })()}
                      {m?.playoff === true && (m?.winnerPenalties != null && String(m.winnerPenalties).trim() !== '' || pred.predictedWinnerPenalties) && (() => {
                        const penActual = m?.winnerPenalties != null ? String(m.winnerPenalties).trim() : ''
                        const penPred = pred.predictedWinnerPenalties != null ? String(pred.predictedWinnerPenalties).trim() : ''
                        const penName = penActual || (!hideFuture && penPred ? penPred : '') || '–'
                        return (
                          <span className="text-muted-foreground">
                            {t('stats.winnerPenalties')} ({penName}):{' '}
                            <span className="font-semibold text-foreground">
                              {hideFuture ? 0 : (pred.predictedWinnerPenaltiesPoint ?? 0)}
                            </span>
                          </span>
                        )
                      })()}
                      <span
                        className={`ml-auto inline-flex items-center px-3 py-1 rounded-lg font-bold ${
                          hideFuture || (pred.totalPoints ?? 0) === 0
                            ? 'bg-destructive/20 text-destructive'
                            : 'bg-success/20 text-success'
                        }`}
                      >
                        {formatPoints(hideFuture ? 0 : (pred.totalPoints ?? 0), t)}
                      </span>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="p-3 sm:p-5 border-t border-white/10 bg-white/5 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 sm:py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
          >
            {t('stats.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default GamePredictionsModal
